import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useBackgroundImage } from './hooks/useBackgroundImage';
import { useColorScheme } from './hooks/useColorScheme';
import { ThemeAwareSvgBackground } from './components/ThemeAwareSvgBackground';
import { ColorSchemeSelector } from './components/ColorSchemeSelector';

// Lazy load all pages
const PortalDashboard = lazy(() => import('./pages/PortalDashboard').then(m => ({ default: m.PortalDashboard })));
const UsersPage = lazy(() => import('./pages/UsersPage').then(m => ({ default: m.UsersPage })));
const UnauthorizedPage = lazy(() => import('./pages/UnauthorizedPage').then(m => ({ default: m.UnauthorizedPage })));
const BackgroundManagementPage = lazy(() => import('./pages/BackgroundManagementPage').then(m => ({ default: m.BackgroundManagementPage })));
const UserSettingsPage = lazy(() => import('./pages/UserSettingsPage').then(m => ({ default: m.UserSettingsPage })));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const ApplicationsManagement = lazy(() => import('./pages/ApplicationsManagement').then(m => ({ default: m.ApplicationsManagement })));

function AppRoutes() {
  const { loading, authenticated } = useAuth();
  const { colorScheme, setColorScheme } = useColorScheme();

  // Apply background image globally - only after authenticated
  const { useThemeAwareBackground } = useBackgroundImage(authenticated);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      {/* Render theme-aware SVG background when using default background */}
      {useThemeAwareBackground && <ThemeAwareSvgBackground colorScheme={colorScheme} />}

      {/* Color scheme selector - only show when using default background */}
      {useThemeAwareBackground && <ColorSchemeSelector onColorChange={setColorScheme} />}

      <Suspense fallback={
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      }>
        <Routes>
          <Route path="/unauthorized" element={<UnauthorizedPage />} />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <PortalDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <UserSettingsPage />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRoles={['portal-admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/users"
            element={
              <ProtectedRoute requiredRoles={['portal-admin']}>
                <UsersPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/applications"
            element={
              <ProtectedRoute requiredRoles={['portal-admin']}>
                <ApplicationsManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/backgrounds"
            element={
              <ProtectedRoute requiredRoles={['portal-admin']}>
                <BackgroundManagementPage />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
